import { useEffect, useRef } from 'react';
import { useElements, useStripe } from '@stripe/react-stripe-js';

import {
  getPharmacyBilling,
  PaymentPlans,
  pharmacyBillingSelector,
  resetBilling,
  setPaymentLoad,
  StripePaymentStatus,
} from '@pharmaplan/common';

import { showError, showSuccess } from '../../components/Admin/Profile/Pharmacist/PharmacistViewProfile/helper';
import {
  Constants,
  DefaultCountry,
  planTypeQuery,
} from '../../helpers/Constants';
import { useAppDispatch } from '../useAppDispatch';
import { useAppSelector } from '../useAppSelector';
import strings from '../../localization';
import { showPaymentError } from '../../components/PaymentGateway/PaymentGateway';

const { succeeded, processing, requirePaymentMethod, canceled } = StripePaymentStatus;
const MAX_CHECKS = 5;

const usePaymentComponent = (paymentPlan: PaymentPlans) => {
  const dispatch = useAppDispatch();

  const stripe = useStripe();
  const elements = useElements();

  const billing = useAppSelector(pharmacyBillingSelector);

  const keepChecking = useRef(false);
  const last4Ref = useRef('');
  const maxChecks = useRef(0);

  const successReturnUrl = `${process.env.REACT_APP_REDIRECT_URL}${Constants.paths.pharmacyBilling}?${planTypeQuery}=${paymentPlan}`;

  const clearData = () => {
    maxChecks.current = 0;
  };

  const handleErrors = (message: string) => {
    clearData();
    dispatch(setPaymentLoad(false));
    showPaymentError(dispatch, message);
  };

  const checkForBilling = () => {
    dispatch(getPharmacyBilling()).then(() => {
      if (maxChecks.current > MAX_CHECKS) {
        clearData();
        dispatch(setPaymentLoad(false));
        showError(dispatch, strings.paymentInProgressIssue);
        keepChecking.current = false;
        return;
      }
      if (last4Ref.current) {
        keepChecking.current = false;
        showSuccess(dispatch, strings.subscribedToPlanContract);
        dispatch(setPaymentLoad(false));
        clearData();
        return;
      }
      if (keepChecking) {
        maxChecks.current += 1;
        setTimeout(checkForBilling, 1000);
      }
    });
  };

  const handleStatus = (status: StripePaymentStatus, message: string) => {
    switch (status) {
      case succeeded:
        keepChecking.current = true;
        checkForBilling();
        break;
      case processing:
        handleErrors(strings.paymentInProgress);
        break;
      case canceled:
      case requirePaymentMethod:
        dispatch(resetBilling());
        handleErrors(message);
        break;
      default:
        dispatch(setPaymentLoad(false));
        break;
    }
  };

  const confirmSetup = async (clientSecret: string) => {
    if (!stripe || !elements) return;

    try {
      const { error, setupIntent } = (await stripe?.confirmSetup?.({
        elements,
        clientSecret,
        redirect: 'if_required',
        confirmParams: {
          return_url: successReturnUrl,
          payment_method_data: {
            billing_details: { address: { country: DefaultCountry } },
          },
        },
      })) ?? {};

      if (error) {
        handleErrors(error.message ?? '');
        return;
      }

      if (setupIntent) {
        handleStatus(
          setupIntent.status as StripePaymentStatus,
          setupIntent.description ?? '',
        );
      }
    } catch (err: unknown) {
      handleErrors(
        (err as { message?: string })?.message ?? strings.anUnknownErrorOccurred,
      );
    }
  };

  const confirmPayment = async (clientSecret: string) => {
    if (!stripe || !elements) return;

    try {
      const { error, paymentIntent } = (await stripe?.confirmPayment?.({
        elements,
        clientSecret,
        redirect: 'if_required',
        confirmParams: {
          return_url: successReturnUrl,
          payment_method_data: {
            billing_details: { address: { country: DefaultCountry } },
          },
        },
      })) ?? {};

      if (error) {
        handleErrors(error.message ?? '');
        return;
      }

      if (paymentIntent) {
        handleStatus(
          paymentIntent.status as StripePaymentStatus,
          paymentIntent.description ?? '',
        );
      }
    } catch (err: unknown) {
      handleErrors(
        (err as { message?: string })?.message ?? strings.anUnknownErrorOccurred,
      );
    }
  };

  const paymentConfig = {
    [PaymentPlans.basic]: { confirmStripePayment: confirmSetup },
    [PaymentPlans.unlimited]: { confirmStripePayment: confirmPayment },
  };

  useEffect(() => {
    last4Ref.current = billing.creditCardLast4 ?? '';
  }, [billing.creditCardLast4]);

  return { paymentConfig };
};

export default usePaymentComponent;
